import { Config } from '../../config';

import type { Point, Transformation } from '../../../types';
import { Migrator } from '../../migrations/migrator';
import { partialSceneDesigns } from './partialsAssembly';

/**
 * Returns the 'partial' scene design configuration as an JS object for the given scene design.
 *
 * Note that if `aspectRatio` is undefined, then _some_ aspect ratio will be chosen.
 * This is only useful when looking for values that are common for all scene designs, e.g. text_1 graphic style.
 *
 * @returns {object}
 * @deprecated in Scalable Templates, use alternate method
 */
export function getPartialSceneDesignConfig(sceneDesign: string, aspectRatio?: string) {
    // extract template from scene design
    const templateId = Config.getTemplateIdFromSceneDesignId(sceneDesign);

    if (!Object.keys(partialSceneDesigns).includes(templateId)) {
        throw new Error(`Template "${templateId}" does not exist.`);
    }

    const sceneDesignsByAspectRatio = partialSceneDesigns[templateId][sceneDesign];

    if (!aspectRatio) {
        aspectRatio = Object.getOwnPropertyNames(sceneDesignsByAspectRatio)[0];
    }

    let config = partialSceneDesigns[templateId][sceneDesign][aspectRatio];

    // migrate config
    config = Migrator.updateSceneData(config);

    return config;
}

export function getBrandSceneDesignConfig(sceneDesign: string, aspectRatio?: string) {
    if (!sceneDesign.endsWith(':brand_1')) {
        throw new Error(`Scene design "${sceneDesign}" must be a brand scene design.`);
    }

    const internalTemplateId = 'internal';
    const internalSceneDesignId = 'internal:brand_1';

    // extract template from scene design
    const sceneDesignsByAspectRatio = partialSceneDesigns[internalTemplateId][internalSceneDesignId];

    if (!aspectRatio) {
        aspectRatio = Object.getOwnPropertyNames(sceneDesignsByAspectRatio)[0];
    }

    let config = partialSceneDesigns[internalTemplateId][internalSceneDesignId][aspectRatio];
    config.sceneDesign = sceneDesign;

    // migrate config
    config = Migrator.updateSceneData(config);

    return config;
}

/**
 * Calculates the position of an object based on its transformation and dimensions.
 */
export function getPositionFromTransformation(
    transformation: Transformation, objWidth: number, objHeight: number
): Point {
    let x = transformation.position.x;
    let y = transformation.position.y;

    // Adjust the position according to the anchor
    x -= transformation.anchor.x * objWidth;
    y -= transformation.anchor.y * objHeight;

    // Make sure numbers are actually numbers
    if (isNaN(x) || isNaN(y)) {
        throw new Error(`x: (${x}, ${y}) are not valid numbers`);
    }

    return { x, y };
}
